// ======================================================================
// FILE: src/modules/finance/layout/FinanceSidebar.jsx
// MODULE: Finance & Accounts Management System
// PURPOSE:
//   Sidebar navigation for Finance module
//
// FEATURES:
//   - RBAC driven
//   - ERP standard layout
//   - Collapsible sidebar
//   - Active menu highlighting
//   - Finance module navigation
//
// AUTHOR: DizyPro ERP
// ======================================================================

import React, {
  useContext,
  useEffect,
  useState,
} from "react";

import {
  NavLink,
  useLocation,
  Link,
} from "react-router-dom";

import { AuthContext }
from "../../../context/AuthContext";

// ----------------------------------------------------------------------
// PERMISSION HELPER
// ----------------------------------------------------------------------

const canAny = (
  permissions,
  keys = []
) =>
  keys.some(
    (k) => permissions?.[k]?.view === true
  );

// ----------------------------------------------------------------------
// MENU
// ----------------------------------------------------------------------

const MENU = [

  // ==========================================================
  // DASHBOARD
  // ==========================================================

  {
    label: "Dashboard",
    route: "/finance/dashboard",
    perms: ["FIN_DASHBOARD"],
    icon: "fas fa-chart-line",
  },

  // ==========================================================
  // TRANSACTIONS
  // ==========================================================

  {
    type: "group",
    label: "Transactions",
  },

  {
    label: "Manual Journal",
    route: "/finance/manual-journal",
    perms: ["FIN_MANUAL_JOURNAL"],
    perms: ["WMS_DASHBOARD"],
   
    icon: "fas fa-book",
  },

  {
    label: "Payment Voucher",
    route: "/finance/payment-vouchers",
    perms: ["FIN_PAYMENT_VOUCHER"],
    perms: ["WMS_DASHBOARD"],
    icon: "fas fa-money-check-alt",
  },

  {
    label: "Receipt Voucher",
    route: "/finance/receipt-vouchers",
    perms: ["FIN_RECEIPT_VOUCHER"],
    perms: ["WMS_DASHBOARD"],
    icon: "fas fa-hand-holding-usd",
  },

  {
    label: "Contra Voucher",
    route: "/finance/contra-vouchers",
    perms: ["FIN_CONTRA_VOUCHER"],
    perms: ["WMS_DASHBOARD"],
    icon: "fas fa-exchange-alt",
  },

  {
    label: "Debit Note",
    route: "/finance/debit-notes",
    perms: ["FIN_DEBIT_NOTE"],
    perms: ["WMS_DASHBOARD"],
    icon: "fas fa-file-invoice",
  },

  {
    label: "Credit Note",
    route: "/finance/credit-notes",
    perms: ["FIN_CREDIT_NOTE"],
    perms: ["WMS_DASHBOARD"],
    icon: "fas fa-file-invoice-dollar",
  },
  {
  label: "Payables",
  route: "/finance/payables",
  perms: ["WMS_DASHBOARD"],
  icon: "fas fa-file-invoice-dollar",
},
{
    label: "Receivables",
    route: "/finance/receivables",
    perms: ["FIN_RECEIPT_VOUCHER"],
    perms: ["WMS_DASHBOARD"],
    icon: "fas fa-hand-holding-usd",
  },

  // ==========================================================
  // MASTERS
  // ==========================================================

  {
    type: "group",
    label: "Masters",
  },

  {
    label: "Company",
    route: "/finance/company",
    perms: ["FIN_COMPANY"],
    perms: ["WMS_DASHBOARD"],
    icon: "fas fa-building",
  },

  {
    label: "Financial Year",
    route: "/finance/financial-years",
    perms: ["FIN_FINANCIAL_YEAR"],
    perms: ["WMS_DASHBOARD"],
    icon: "fas fa-calendar-alt",
  },

  {
    label: "Financial Period",
    route: "/finance/financial-periods",
    perms: ["FIN_FINANCIAL_PERIOD"],
    perms: ["WMS_DASHBOARD"],
    icon: "fas fa-calendar-week",
  },

  {
    label: "Currency",
    route: "/finance/currencies",
    perms: ["FIN_CURRENCY"],
    perms: ["WMS_DASHBOARD"],
    icon: "fas fa-coins",
  },

  {
    label: "Exchange Rates",
    route: "/finance/exchange-rates",
    perms: ["FIN_EXCHANGE_RATE"],
    perms: ["WMS_DASHBOARD"],
    icon: "fas fa-dollar-sign",
  },

  {
    label: "Account Groups",
    route: "/finance/account-groups",
    perms: ["FIN_ACCOUNT_GROUP"],
    perms: ["WMS_DASHBOARD"],
    icon: "fas fa-folder-open",
  },

  {
    label: "Chart of Accounts",
    route: "/finance/chart-of-accounts",
    perms: ["FIN_CHART_OF_ACCOUNT"],
    perms: ["WMS_DASHBOARD"],
    icon: "fas fa-sitemap",
  },

  {
    label: "Cost Centers",
    route: "/finance/cost-centers",
    perms: ["FIN_COST_CENTER"],
    perms: ["WMS_DASHBOARD"],
    icon: "fas fa-project-diagram",
  },

  {
    label: "Banks",
    route: "/finance/banks",
    perms: ["FIN_BANK"],
    perms: ["WMS_DASHBOARD"],
    icon: "fas fa-university",
  },

  {
    label: "Bank Accounts",
    route: "/finance/bank-accounts",
    perms: ["FIN_BANK_ACCOUNT"],
    perms: ["WMS_DASHBOARD"],
    icon: "fas fa-credit-card",
  },

  {
    label: "Finance Events",
    route: "/finance/finance-events",
    perms: ["FIN_FINANCE_EVENT"],
    perms: ["WMS_DASHBOARD"],
    icon: "fas fa-bolt",
  },

  {
    label: "Posting Rules",
    route: "/finance/posting-rules",
    perms: ["FIN_POSTING_RULE"],
    perms: ["WMS_DASHBOARD"],
    icon: "fas fa-cogs",
  },

  // ==========================================================
  // REPORTS
  // ==========================================================

  {
    type: "group",
    label: "Reports",
  },

  {
    label: "General Ledger",
    route: "/finance/reports/general-ledger",
    perms: ["FIN_REPORT"],
    perms: ["WMS_DASHBOARD"],
    icon: "fas fa-book-open",
  },

  {
    label: "Trial Balance",
    route: "/finance/reports/trial-balance",
    perms: ["FIN_REPORT"],
    perms: ["WMS_DASHBOARD"],
    icon: "fas fa-balance-scale",
  },

  {
    label: "Profit & Loss",
    route: "/finance/reports/profit-loss",
    perms: ["FIN_REPORT"],
    perms: ["WMS_DASHBOARD"],
    icon: "fas fa-chart-line",
  },

  {
    label: "Balance Sheet",
    route: "/finance/reports/balance-sheet",
    perms: ["FIN_REPORT"],
    perms: ["WMS_DASHBOARD"],
    icon: "fas fa-file-alt",
  },

  {
    label: "Cash Flow",
    route: "/finance/reports/cash-flow",
    perms: ["FIN_REPORT"],
    perms: ["WMS_DASHBOARD"],
    icon: "fas fa-water",
  },

  {
    label: "Vendor Ledger",
    route: "/finance/reports/vendor-ledger",
    perms: ["FIN_REPORT"],
    perms: ["WMS_DASHBOARD"],
    icon: "fas fa-book",
  },
  
  {
  label: "Customer Ledger",
  route: "/finance/reports/customer-ledger",
  perms: ["FIN_REPORT"],
  perms: ["WMS_DASHBOARD"],
  icon: "fas fa-book",
},
  // ==========================================================
  // CONFIGURATION
  // ==========================================================

  {
    type: "group",
    label: "Configuration",
  },

  {
    label: "Settings",
    route: "/finance/settings",
    perms: ["FIN_SETTINGS"],
    perms: ["WMS_DASHBOARD"],
    icon: "fas fa-sliders-h",
  },

];
// ======================================================================
// COMPONENT
// ======================================================================

export default function FinanceSidebar({
  onToggle,
}) {

  const { permissions } =
    useContext(AuthContext);

  const location =
    useLocation();

  const collapsedLS =
    localStorage.getItem(
      "finance_sidebar_collapsed"
    );

  const [expanded, setExpanded] =
    useState(
      collapsedLS === null
        ? true
        : collapsedLS === "false"
    );

  useEffect(() => {

    localStorage.setItem(
      "finance_sidebar_collapsed",
      expanded ? "false" : "true"
    );

    onToggle?.(expanded);

  }, [expanded, onToggle]);

  return (

    <aside
      style={{
        width: expanded ? 260 : 64,
        transition: "width 0.22s ease",
        background: "#111827",
        color: "#fff",
        minHeight: "100vh",
        position: "fixed",
        left: 0,
        top: 0,
        zIndex: 100,
      }}
    >

      {/* ========================================================== */}
      {/* HEADER */}
      {/* ========================================================== */}

      <div
        style={{
          height: 56,
          display: "flex",
          alignItems: "center",
          justifyContent: expanded
            ? "space-between"
            : "center",
          padding: expanded
            ? "0 12px"
            : "0",
          borderBottom:
            "1px solid #374151",
        }}
      >

        {expanded && (
          <span
            style={{
              fontWeight: 700,
            }}
          >
            Finance
          </span>
        )}

        <button
          onClick={() =>
            setExpanded((v) => !v)
          }
          style={{
            background: "none",
            border: "none",
            color: "#9ca3af",
            cursor: "pointer",
          }}
        >
          <i className="fas fa-bars" />
        </button>

      </div>

      {/* ========================================================== */}
      {/* BACK TO DASHBOARD */}
      {/* ========================================================== */}

      <Link
        to="/dashboard"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          margin: 12,
          padding: "10px 12px",
          borderRadius: 999,
          background: "#2563eb",
          color: "#fff",
          textDecoration: "none",
          justifyContent: expanded
            ? "flex-start"
            : "center",
        }}
      >
        <i className="fas fa-home" />

        {expanded && (
          <span>
            Back to Dashboard
          </span>
        )}

      </Link>

      {/* ========================================================== */}
      {/* MENU */}
      {/* ========================================================== */}

      <nav
        style={{
          maxHeight:
            "calc(100vh - 140px)",
          overflowY: "auto",
        }}
      >

        {MENU.map((item, idx) => {

          // --------------------------------------------------------
          // GROUP HEADER
          // --------------------------------------------------------

          if (
            item.type === "group"
          ) {

            if (!expanded)
              return null;

            return (

              <div
                key={`grp-${idx}`}
                style={{
                  margin:
                    "12px 14px 6px",
                  fontSize: 11,
                  color: "#9ca3af",
                  textTransform:
                    "uppercase",
                }}
              >
                {item.label}
              </div>

            );

          }

          // --------------------------------------------------------
          // PERMISSION CHECK
          // --------------------------------------------------------

          if (
            !canAny(
              permissions,
              item.perms
            )
          ) {

            return null;

          }

          // --------------------------------------------------------
          // ACTIVE MENU
          // --------------------------------------------------------

          const active =
            location.pathname.startsWith(
              item.route
            );

          return (
                      

            <NavLink
              key={item.route}
              to={item.route}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: expanded
                  ? "10px 14px"
                  : "10px 0",
                justifyContent:
                  expanded
                    ? "flex-start"
                    : "center",
                color: active
                  ? "#fff"
                  : "#cbd5e1",
                background: active
                  ? "#374151"
                  : "transparent",
                textDecoration:
                  "none",
                fontSize: 14,
              }}
            >

              <i
                className={
                  item.icon
                }
              />

              {expanded && (

                <span>
                  {item.label}
                </span>

              )}

            </NavLink>

          );

        })}

      </nav>

    </aside>

  );

}